customElements.get('scrollytelling-video') ||
  customElements.define(
    'scrollytelling-video',
    class extends HTMLElement {
      constructor() {
        super();
        this.video = this.querySelector('.wt-scrollytelling__video-source');
        this.canvas = this.querySelector('.wt-scrollytelling__canvas');
        this.blocks = this.querySelectorAll('.wt-scrollytelling__block');
        this.imageFramesContainer = this.querySelector('.wt-scrollytelling__image-frames');
        this.imageFrames = this.imageFramesContainer
          ? this.imageFramesContainer.querySelectorAll('.wt-scrollytelling__frame')
          : [];
        this.mediaType = this.dataset.mediaType || 'video';
        this.mobileFallback = this.dataset.mobileFallback === 'true';
        this.androidOnlyFallback = this.dataset.androidOnlyFallback !== 'false';
        this.designMode = this.dataset.designMode === 'true';
 
        // Tweakables
        this.FPS = parseFloat(this.dataset.fps) || 30; // frame rate of the source video
        this.SMOOTHING = 0.18; // 0..1 per 60 Hz frame; higher = snappier
 
        this.state = {
          currentProgress: 0,
          targetProgress: 0,
          isReady: false,
          isSeeking: false,
          pendingTime: null,
          lastRequestedFrame: -1,
          isMobile: window.innerWidth < 768,
          isAndroid: typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent),
        };
 
        this.isPlaceholderMode = !this.video;
        this.useImageMode = this.computeUseImageMode();
        this._raf = null;
        this._lastTs = 0;
        this.animate = this.animate.bind(this);
 
        if (!this.isPlaceholderMode && this.canvas) {
          // FIX 1: no willReadFrequently -> GPU-accelerated canvas
          this.ctx = this.canvas.getContext('2d', { alpha: true });
          this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        }
 
        if (this.useImageMode && this.imageFrames.length) {
          this.state.isReady = true;
          this.applyImageModeVisibility(true);
          this.loadFallbackImages();
        } else if (this.isPlaceholderMode) {
          this.state.isReady = true;
        }
 
        this.init();
      }
 
      computeUseImageMode() {
        if (this.mediaType === 'images') return true;
        return !(!this.mobileFallback || !this.state.isMobile || (this.androidOnlyFallback && !this.state.isAndroid));
      }
 
      loadFallbackImages() {
        if (!this.imageFramesContainer?.classList.contains('wt-scrollytelling__image-frames--fallback')) return;
        this.imageFrames.forEach((frame) => {
          const img = frame.querySelector('.wt-scrollytelling__frame-img[data-src]');
          if (img && img.dataset.src) {
            img.src = img.dataset.src;
            if (img.dataset.srcset) img.srcset = img.dataset.srcset;
            if (img.dataset.sizes) img.sizes = img.dataset.sizes;
          }
        });
      }
 
      applyImageModeVisibility(useImageMode) {
        if (this.video) this.video.style.display = useImageMode ? 'none' : '';
        if (this.canvas) this.canvas.style.display = useImageMode ? 'none' : '';
        if (this.imageFramesContainer)
          this.imageFramesContainer.classList.toggle('wt-scrollytelling__image-frames--active', useImageMode);
      }
 
      init() {
        if (!this.useImageMode && !this.isPlaceholderMode) {
          this.setupVideo();
          this.applyImageModeVisibility(false);
        } else if (this.useImageMode) {
          this.applyImageModeVisibility(true);
        }
 
        window.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
        window.addEventListener('resize', this.onResize.bind(this));
        this.onScroll();
 
        this.isVisible = false;
        new IntersectionObserver(
          (entries) => {
            this.isVisible = entries[0].isIntersecting;
            if (this.isVisible) this.startLoop();
          },
          { threshold: 0.01 }
        ).observe(this);
      }
 
      setupVideo() {
        if (!this.video) return;
        const src = this.state.isMobile ? this.video.dataset.videoMobile : this.video.dataset.videoDesktop;
        if (!src) return;
 
        if (this._onLoadedData) {
          this.video.removeEventListener('loadeddata', this._onLoadedData);
          this.video.removeEventListener('seeked', this._onSeeked);
          this.video.removeEventListener('error', this._onSeeked);
        }
 
        this.state.isSeeking = false;
        this.state.pendingTime = null;
        this.state.lastRequestedFrame = -1;
 
        this.video.muted = true;
        this.video.pause();
        this.video.src = src;
        this.video.load();
 
        this._onLoadedData = () => {
          this.video.pause();
          if (this.canvas) {
            this.canvas.width = this.video.videoWidth;
            this.canvas.height = this.video.videoHeight;
          }
          this.state.isReady = true;
          this.seekTo(this.state.currentProgress * this.video.duration);
        };
 
        // FIX 2 + 6: when a seek finishes, draw it, then immediately apply the newest target (if any)
        this._onSeeked = () => {
          this.state.isSeeking = false;
          this.renderCanvas();
          if (this.state.pendingTime !== null) {
            const t = this.state.pendingTime;
            this.state.pendingTime = null;
            this.seekTo(t);
          }
        };
 
        this.video.addEventListener('loadeddata', this._onLoadedData);
        this.video.addEventListener('seeked', this._onSeeked);
        this.video.addEventListener('error', this._onSeeked);
      }
 
      // FIX 3: frame-snapped, de-duplicated, latest-wins seeking
      seekTo(time) {
        const v = this.video;
        if (!v || !v.duration) return;
        const lastFrame = Math.max(0, Math.floor(v.duration * this.FPS) - 1);
        const frame = Math.min(lastFrame, Math.max(0, Math.round(time * this.FPS)));
        if (frame === this.state.lastRequestedFrame) return;
 
        const snapped = (frame + 0.5) / this.FPS; // middle of the frame avoids boundary rounding
        if (this.state.isSeeking) {
          this.state.pendingTime = snapped;
          return;
        }
        this.state.lastRequestedFrame = frame;
        this.state.isSeeking = true;
        v.currentTime = Math.min(snapped, v.duration - 0.001);
      }
 
      onScroll() {
        if (this.designMode && this.parentElement?.classList.contains('wt-scrollytelling-wrapper--preview-initial')) {
          this.parentElement.classList.remove('wt-scrollytelling-wrapper--preview-initial');
        }
        const rect = this.parentElement.getBoundingClientRect();
        const totalHeight = rect.height - window.innerHeight;
        this.state.targetProgress = totalHeight > 0 ? Math.max(0, Math.min(1, -rect.top / totalHeight)) : 0;
      }
 
      // FIX 4: guarantee a single rAF loop
      startLoop() {
        if (this._raf !== null) return;
        this._lastTs = 0;
        this._raf = requestAnimationFrame(this.animate);
      }
 
      animate(ts) {
        if (!this.isVisible) {
          this._raf = null;
          return;
        }
 
        if (this.state.isReady) {
          // FIX 5: frame-rate independent smoothing
          const dt = this._lastTs ? Math.min(100, ts - this._lastTs) : 16.67;
          this._lastTs = ts;
          const k = 1 - Math.pow(1 - this.SMOOTHING, dt / 16.67);
 
          const diff = this.state.targetProgress - this.state.currentProgress;
          if (Math.abs(diff) > 0.0001) {
            this.state.currentProgress += diff * k;
          } else {
            this.state.currentProgress = this.state.targetProgress;
          }
 
          if (!this.useImageMode && !this.isPlaceholderMode && this.video?.duration) {
            this.seekTo(this.state.currentProgress * this.video.duration);
          }
 
          const progressPct = this.state.currentProgress * 100;
          this.updateBlocks(progressPct);
          if (this.useImageMode) this.updateImageFrames(progressPct);
        }
 
        this._raf = requestAnimationFrame(this.animate);
      }
 
      getTiming(block) {
        const cs = getComputedStyle(block);
        return {
          start: parseFloat(cs.getPropertyValue('--timing-start')),
          end: parseFloat(cs.getPropertyValue('--timing-end')),
        };
      }
 
      updateImageFrames(progress) {
        const blockCount = this.blocks.length;
        if (!blockCount) return;
        let activeIndex = -1;
        for (let i = 0; i < blockCount; i++) {
          const { start, end } = this.getTiming(this.blocks[i]);
          if (progress >= start && progress <= end) {
            activeIndex = i;
            break;
          }
        }
        if (activeIndex < 0) {
          const firstStart = this.getTiming(this.blocks[0]).start;
          const lastEnd = this.getTiming(this.blocks[blockCount - 1]).end;
          if (progress < firstStart) activeIndex = 0;
          else if (progress > lastEnd) activeIndex = blockCount - 1;
          else {
            for (let i = blockCount - 1; i >= 0; i--) {
              if (progress >= this.getTiming(this.blocks[i]).end) {
                activeIndex = i;
                break;
              }
            }
            if (activeIndex < 0) activeIndex = 0;
          }
        }
        this.imageFrames.forEach((frame, i) => frame.classList.toggle('is-visible', i === activeIndex));
      }
 
      renderCanvas() {
        if (!this.isPlaceholderMode && this.state.isReady && this.video?.readyState >= 2 && this.ctx) {
          this.ctx.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);
        }
      }
 
      updateBlocks(progress) {
        this.blocks.forEach((block) => {
          const { start, end } = this.getTiming(block);
          if (progress >= start && progress <= end) {
            block.classList.add('is-active', 'is-was-active');
          } else {
            block.classList.remove('is-active');
            if (progress < start) block.classList.remove('is-was-active');
          }
        });
      }
 
      onResize() {
        const isMobileNow = window.innerWidth < 768;
        const wasMobile = this.state.isMobile;
        const wasImageMode = this.useImageMode;
        this.state.isMobile = isMobileNow;
        this.useImageMode = this.computeUseImageMode();
 
        if (wasImageMode !== this.useImageMode) {
          if (this.useImageMode && this.imageFrames.length) {
            this.state.isReady = true;
            this.loadFallbackImages();
            this.applyImageModeVisibility(true);
            if (this.video) {
              this.video.removeAttribute('src');
              this.video.load();
            }
          } else {
            this.applyImageModeVisibility(false);
            if (!this.isPlaceholderMode) {
              this.state.isReady = false;
              this.setupVideo();
            }
          }
        } else if (!this.useImageMode && !this.isPlaceholderMode && isMobileNow !== wasMobile) {
          this.setupVideo();
        }
        this.onScroll();
      }
    }
  );